<script setup lang="ts">
import { RouterLink } from "vue-router";
import type { BoxLayout, DisplayMode } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import {
  buildUrlPrefix,
  getProductName,
  getProductRoute,
  getBiggestThumbnailUrl,
  getSmallestThumbnailUrl,
} from "@shopware/helpers";
import { toRefs, computed, ref } from "vue";
import { defu } from "defu";
import SwListingProductPrice from "././../product/SwListingProductPrice.vue";
import {
  useAddToCart,
  useNotifications,
  useProductWishlist,
  useUrlResolver,
} from "#imports";
import { useElementSize } from "@vueuse/core";
import type { Schemas } from "#shopware";
import { ApiClientError } from "@shopware/api-client";
const { t } = useI18n();

const { pushSuccess, pushError, pushWarning } = useNotifications();

const props = withDefaults(
  defineProps<{
    product: Schemas["Product"];
    layoutType?: BoxLayout;
    isProductListing?: boolean;
    displayMode?: DisplayMode;
  }>(),
  {
    layoutType: "standard",
    displayMode: "standard",
    isProductListing: false,
  }
);

type Translations = {
  product: {
    addedToWishlist: string;
    removedFromTheWishlist: string;
    reason: string;
    cannotAddToWishlist: string;
    addedToCart: string;
    addToCart: string;
    details: string;
    badges: {
      topseller: string;
    };
  };
};

let translations: Translations = {
  product: {
    addedToWishlist: "has been added to wishlist.",
    removedFromTheWishlist: "has been removed from wishlist.",
    reason: "Reason",
    cannotAddToWishlist: "cannot be added to wishlist.",
    addedToCart: "has been added to cart.",
    addToCart: "Add to cart",
    details: "Details",
    badges: {
      topseller: "Tip",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { product } = toRefs(props);

const { addToCart } = useAddToCart(product);

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);
const isLoading = ref(false);

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      await addToWishlist();
      return pushSuccess(
        `<b>${props.product?.translated?.name}</b>` +
          t("product.messages.addedToWishlist")
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        const reason = error.details.errors?.[0]?.detail
          ? `${translations.product.reason}: ${error.details.errors?.[0]?.detail}`
          : "";
        return pushError(
          `<b>${props.product?.translated?.name}</b>` +
            t("product.messages.annotAddToWishlist") +
            `\n${reason}`
        );
      }
    }
  }
  pushWarning(
    `<b>${props.product?.translated?.name}</b> ` +
      t("product.messages.removedFromWishlist")
  );
  removeFromWishlist();
};

const addToCartProxy = async () => {
  await addToCart();
  pushSuccess(
    `${props.product?.translated.name} ${translations.product.addedToCart}`
  );
};

const { getUrlPrefix } = useUrlResolver();

const imageElement = ref(null);
const { height } = useElementSize(imageElement);

const DEFAULT_THUMBNAIL_SIZE = 10;
function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed<string>(() => {
  if (product.value?.cover?.media.thumbnails?.length > 0) {
    let thumb = `${getBiggestThumbnailUrl(product.value?.cover?.media)}`;
    if (thumb) {
      return thumb;
    }
  }
  return `${product.value?.cover?.media.url}`;
});
</script>

<template>
  <div class="group py-5 bg-white">
    <figure
      class="w-full h-[14.75rem] mx-auto mb-5 flex items-center justify-center relative"
    >
      <RouterLink
        :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
        class="overflow-hidden"
      >
        <figure
          class="w-[12.75rem] h-[12.75rem] mx-auto mb-5 flex items-center justify-center relative aspect-square"
        >
          <NuxtImg
            ref="imageElement"
            loading="lazy"
            format="webp"
            :src="srcPath"
            width="250"
            :alt="getProductName({ product }) || ''"
            class="object-contain w-full h-full"
            data-testid="product-box-img"
          />
        </figure>
      </RouterLink>
      <span
        :disabled="isLoading"
        @click="toggleWishlistProduct"
        class="fav-icon top-[0] left-[20px] absolute text-black03 text-xl cursor-pointer"
      >
        <i v-if="isInWishlist" class="fa-solid fa-heart"></i>
        <i v-else class="fa-regular fa-heart"></i>
      </span>
      <div
        v-if="product?.isNew"
        class="font-bold bg-skin-theme text-skin-white text-sm px-5 absolute rounded-full top-0 right-2 -ml-5"
      >
        {{ $t("product.badges.new") }}
      </div>
    </figure>
 
    <div class="px-5">
      <p class="text-sm !mb-4">
        {{ $t("product.productNumber") }}: {{ product.productNumber }}
      </p>
      <h4 class="text-base font-bold mb-4 truncate">
        {{ getProductName({ product }) }}
      </h4>
      <p
        class="text-sm !mb-5 truncate-multiline h-[40px]"
        v-html="product.translated.metaDescription"
      ></p>
      <h3 class="font-bold text-2xl mb-5 text-skin-theme" v-if="!product.customFields?.custom_product_fields_hide_price">
        <SwListingProductPrice
          :product="product"
          data-testid="product-box-product-price"
        />
      </h3>
      <div v-else class="h-7"></div>
    </div>
    <div class="mt-5">
      <RouterLink
        :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
        class="btn-outline btn-sm"
      >
        {{ $t("product.details") }}
      </RouterLink>
    </div>
  </div>
</template>
